import { z } from 'zod'
import {
  environmentReferenceSchema,
  httpsUrlSchema,
  httpUrlSchema,
  iconSchema,
  idSchema,
  imageSourceSchema,
  internalPathSchema,
  linkSchema,
  lucideIconNameSchema,
  publicAssetPathSchema,
} from './common'
import {
  isoDateTimeSchema,
  messageOriginSchema,
  messagePagesSchema,
  messageSeveritySchema,
  messageStatusSchema,
} from './message'
import { configuredDashboardSchema, dashboardSchema } from './dashboardWidgets'
import { monitoringYamlSchema } from './monitoring'
import { inventoryYamlSchema } from './inventoryYaml'

export { iconSchema } from './common'

const badgeSchema = z
  .object({
    label: z.string().min(1).max(28),
    tone: z.enum(['blue', 'green', 'orange', 'red', 'neutral']).default('neutral'),
  })
  .strict()

export const applicationSchema = z
  .object({
    id: idSchema,
    name: z.string().min(1).max(80),
    description: z.string().min(1).max(220),
    url: httpUrlSchema,
    icon: iconSchema.optional(),
    keywords: z.array(z.string().min(1).max(40)).max(20).default([]),
    badge: badgeSchema.optional(),
    category: z.string().min(1).max(48).optional(),
    target: z.enum(['_self', '_blank']).default('_blank'),
    monitoringServiceId: idSchema.optional(),
  })
  .strict()

export const applicationSectionSchema = z
  .object({
    id: idSchema,
    type: z.literal('applications'),
    title: z.string().min(1).max(90),
    description: z.string().min(1).max(220).optional(),
    applications: z.array(applicationSchema).max(100),
  })
  .strict()

const visualLevelSchema = z
  .enum(['neutral', 'info', 'success', 'warning', 'danger'])
  .default('info')
const legacyMessageLevelSchema = z.enum(['neutral', 'info', 'success', 'warning', 'danger'])
const periodSchema = z
  .object({ start: isoDateTimeSchema.optional(), end: isoDateTimeSchema.optional() })
  .strict()
  .refine(
    (period) => !period.start || !period.end || Date.parse(period.start) < Date.parse(period.end),
    { message: 'la date de début doit précéder la date de fin', path: ['end'] },
  )
export const sourceHeaderSchema = z.union([z.string().min(1).max(500), environmentReferenceSchema])

export function isSensitiveHeaderName(name: string) {
  const normalized = name.toLowerCase().replaceAll('_', '-')
  const compact = normalized.replaceAll('-', '')
  return (
    ['authorization', 'cookie', 'proxyauthorization', 'xapikey', 'apikey'].includes(compact) ||
    /(?:auth|token|secret|apikey)/.test(compact)
  )
}

export const dynamicSourceSchema = z
  .object({
    endpoint: httpUrlSchema,
    headers: z
      .record(z.string().min(1).max(100), sourceHeaderSchema)
      .optional()
      .superRefine((headers, context) => {
        Object.entries(headers ?? {}).forEach(([name, value]) => {
          if (isSensitiveHeaderName(name) && typeof value === 'string') {
            context.addIssue({
              code: 'custom',
              path: [name],
              message: 'un en-tête sensible doit utiliser une référence env',
            })
          }
        })
      }),
    timeout: z.number().int().min(100).max(15000).default(3000),
  })
  .strict()

const messageDynamicFields = {
  title: z.string().min(1).max(120).optional(),
  content: z.string().min(1).max(20_000).optional(),
  author: z.string().min(1).max(100).optional(),
  severity: messageSeveritySchema.optional(),
  startAt: isoDateTimeSchema.optional(),
  endAt: isoDateTimeSchema.optional(),
  enabled: z.boolean().optional(),
  pages: messagePagesSchema.optional(),
  priority: z.number().int().min(0).max(100).optional(),
  dismissible: z.boolean().optional(),
  icon: iconSchema.optional(),
  link: linkSchema.optional(),
  level: legacyMessageLevelSchema.optional(),
  period: periodSchema.optional(),
}

function validateMessageCompatibility(
  message: {
    severity?: string
    level?: string
    startAt?: string
    endAt?: string
    period?: unknown
  },
  context: z.RefinementCtx,
) {
  if (message.severity !== undefined && message.level !== undefined)
    context.addIssue({
      code: 'custom',
      path: ['severity'],
      message: 'severity et level ne peuvent pas être utilisés ensemble',
    })
  if (
    (message.startAt !== undefined || message.endAt !== undefined) &&
    message.period !== undefined
  )
    context.addIssue({
      code: 'custom',
      path: ['startAt'],
      message: 'startAt/endAt et period ne peuvent pas être utilisés ensemble',
    })
  if (message.startAt && message.endAt && Date.parse(message.startAt) >= Date.parse(message.endAt))
    context.addIssue({
      code: 'custom',
      path: ['endAt'],
      message: 'la date de début doit précéder la date de fin',
    })
}

export const dynamicMessagePayloadSchema = z
  .object(messageDynamicFields)
  .strict()
  .superRefine(validateMessageCompatibility)
export const messageSectionSchema = z
  .object({
    id: idSchema,
    type: z.literal('message'),
    ...messageDynamicFields,
    source: dynamicSourceSchema.optional(),
  })
  .strict()
  .superRefine((section, context) => {
    validateMessageCompatibility(section, context)
    if (!section.source && (!section.title || !section.content))
      context.addIssue({
        code: 'custom',
        message: 'title et content sont requis sans source dynamique',
      })
  })

const announcementFields = {
  title: z.string().min(1).max(120),
  content: z.string().min(1).max(4000),
  level: visualLevelSchema,
  icon: iconSchema.optional(),
  link: linkSchema.optional(),
  period: periodSchema.optional(),
}
export const dynamicAnnouncementPayloadSchema = z
  .object({
    title: announcementFields.title.optional(),
    content: announcementFields.content.optional(),
    level: visualLevelSchema.optional(),
    icon: announcementFields.icon,
    link: announcementFields.link,
    period: announcementFields.period,
  })
  .strict()
export const dynamicSectionPayloadSchema = dynamicAnnouncementPayloadSchema
export const announcementSectionSchema = z
  .object({
    id: idSchema,
    type: z.literal('announcement'),
    title: announcementFields.title.optional(),
    content: announcementFields.content.optional(),
    level: announcementFields.level,
    icon: announcementFields.icon,
    link: announcementFields.link,
    period: announcementFields.period,
    source: dynamicSourceSchema.optional(),
  })
  .strict()
  .superRefine((section, context) => {
    if (!section.source && (!section.title || !section.content))
      context.addIssue({
        code: 'custom',
        message: 'title et content sont requis sans source dynamique',
      })
  })

export const resolvedMessageSectionSchema = z
  .object({
    id: idSchema,
    type: z.literal('message'),
    title: z.string().min(1).max(120),
    contentHtml: z.string().max(100_000),
    author: z.string().min(1).max(100).optional(),
    severity: messageSeveritySchema,
    startAt: isoDateTimeSchema.optional(),
    endAt: isoDateTimeSchema.optional(),
    enabled: z.boolean(),
    pages: messagePagesSchema,
    priority: z.number().int().min(0).max(100),
    dismissible: z.boolean(),
    icon: iconSchema.optional(),
    link: linkSchema.optional(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
    revision: z.number().int().positive(),
    origin: messageOriginSchema,
    status: messageStatusSchema,
    level: messageSeveritySchema,
    period: periodSchema.optional(),
  })
  .strict()
export const resolvedAnnouncementSectionSchema = z
  .object({
    id: idSchema,
    type: z.literal('announcement'),
    ...announcementFields,
    contentHtml: z.string().max(100_000),
  })
  .strict()
export const configuredSectionSchema = z.union([
  applicationSectionSchema,
  messageSectionSchema,
  announcementSectionSchema,
])
export const sectionSchema = z.discriminatedUnion('type', [
  applicationSectionSchema,
  resolvedMessageSectionSchema,
  resolvedAnnouncementSectionSchema,
])

const navigationIconInputSchema = z.union([iconSchema, lucideIconNameSchema])
const legacyNavigationItemSchema = z
  .object({
    label: z.string().min(1).max(60),
    path: internalPathSchema,
    icon: iconSchema.optional(),
  })
  .strict()
const serverInternalNavigationItemSchema = z
  .object({
    id: idSchema,
    label: z.string().min(1).max(60),
    icon: navigationIconInputSchema.optional(),
    path: internalPathSchema,
  })
  .strict()
const serverExternalNavigationItemSchema = z
  .object({
    id: idSchema,
    label: z.string().min(1).max(60),
    icon: navigationIconInputSchema.optional(),
    url: httpUrlSchema,
    target: z.enum(['_self', '_blank']).default('_blank'),
  })
  .strict()
export const serverNavigationItemSchema = z.union([
  serverInternalNavigationItemSchema,
  serverExternalNavigationItemSchema,
])
export const serverNavigationSchema = z.union([
  z.array(legacyNavigationItemSchema).max(20),
  z
    .object({
      enabled: z.boolean().default(true),
      links: z.array(serverNavigationItemSchema).max(20).default([]),
    })
    .strict(),
])
const publicInternalNavigationItemSchema = z
  .object({
    id: idSchema,
    label: z.string().min(1).max(60),
    icon: iconSchema.optional(),
    path: internalPathSchema,
  })
  .strict()
const publicExternalNavigationItemSchema = z
  .object({
    id: idSchema,
    label: z.string().min(1).max(60),
    icon: iconSchema.optional(),
    url: httpUrlSchema,
    target: z.enum(['_self', '_blank']),
  })
  .strict()
export const navigationItemSchema = z.union([
  publicInternalNavigationItemSchema,
  publicExternalNavigationItemSchema,
])
export const publicNavigationSchema = z
  .object({ enabled: z.boolean(), links: z.array(navigationItemSchema).max(20) })
  .strict()
const pageLayoutField = { layout: z.enum(['classic', 'dashboard']).optional() }
const configuredPageSchema = z
  .object({
    id: idSchema,
    path: internalPathSchema,
    title: z.string().min(1).max(100),
    description: z.string().min(1).max(220).optional(),
    sections: z.array(configuredSectionSchema).max(30),
    ...pageLayoutField,
    dashboard: configuredDashboardSchema.optional(),
  })
  .strict()
const pageSchema = z
  .object({
    id: idSchema,
    path: internalPathSchema,
    title: z.string().min(1).max(100),
    description: z.string().min(1).max(220).optional(),
    sections: z.array(sectionSchema).max(30),
    ...pageLayoutField,
    dashboard: dashboardSchema.optional(),
  })
  .strict()
export const DEFAULT_BRANDING = {
  logo: '/morning-mark.svg',
  favicon: '/morning-mark.svg',
  appleTouchIcon: '/morning-mark.svg',
  pwaIcon192: '/morning-pwa-192.svg',
  pwaIcon512: '/morning-pwa-512.svg',
} as const

export const brandingSchema = z
  .object({
    logo: imageSourceSchema.default(DEFAULT_BRANDING.logo),
    favicon: publicAssetPathSchema.default(DEFAULT_BRANDING.favicon),
    appleTouchIcon: publicAssetPathSchema.default(DEFAULT_BRANDING.appleTouchIcon),
    pwaIcon192: publicAssetPathSchema.default(DEFAULT_BRANDING.pwaIcon192),
    pwaIcon512: publicAssetPathSchema.default(DEFAULT_BRANDING.pwaIcon512),
  })
  .strict()
  .default(DEFAULT_BRANDING)

export const connectivitySchema = z
  .object({
    enabled: z.boolean().default(true),
    intervalSeconds: z.number().int().min(30).max(60).default(45),
  })
  .strict()
  .default({ enabled: true, intervalSeconds: 45 })

export const backgroundSchema = z
  .object({
    image: z.union([publicAssetPathSchema, httpUrlSchema]).nullable().default(null),
  })
  .strict()
  .default({ image: null })

export const gridSchema = z
  .object({
    minCardWidth: z.number().int().min(180).max(420).default(270),
    maxColumns: z.number().int().min(1).max(8).nullable().default(null),
  })
  .strict()
  .default({ minCardWidth: 270, maxColumns: null })

const customStylesheetSchema = z
  .union([publicAssetPathSchema, httpsUrlSchema])
  .nullable()
  .default(null)
export const advancedAppearanceSchema = z
  .object({
    customStylesheet: customStylesheetSchema,
  })
  .strict()
  .default({ customStylesheet: null })

export const appearanceSchema = z
  .object({
    theme: z.enum(['light', 'dark', 'auto']).default('auto'),
    accent: z
      .string()
      .regex(/^#[0-9a-f]{6}$/i, 'doit être une couleur hexadécimale sur 6 caractères')
      .default('#1677e8'),
    view: z.enum(['compact', 'modern', 'list']).default('modern'),
    background: backgroundSchema,
    grid: gridSchema,
    advanced: advancedAppearanceSchema,
  })
  .strict()

export const searchSchema = z
  .object({
    enabled: z.boolean().default(true),
    placeholder: z.string().min(1).max(100),
    shortcut: z.enum(['auto', 'mod-k', 'slash', 'none']).default('auto'),
  })
  .strict()

export const changelogTenantPolicySchema = z
  .object({ forceCriticalReleases: z.boolean().default(false) })
  .strict()
  .default({ forceCriticalReleases: false })

export const serverAppSchema = z
  .object({
    name: z.string().min(1).max(50),
    description: z.string().min(1).max(160),
    locale: z.string().min(2).max(35).default('fr-FR'),
    appearance: appearanceSchema,
    header: z
      .object({
        eyebrow: z.string().min(1).max(60),
        title: z.string().min(1).max(100),
        subtitle: z.string().min(1).max(220),
        showDate: z.boolean().default(true),
      })
      .strict(),
    search: searchSchema,
    branding: brandingSchema,
    connectivity: connectivitySchema,
    changelog: changelogTenantPolicySchema,
  })
  .strict()

export const publicAppSchema = z
  .object({
    name: serverAppSchema.shape.name,
    description: serverAppSchema.shape.description,
    locale: serverAppSchema.shape.locale,
    appearance: serverAppSchema.shape.appearance,
    header: serverAppSchema.shape.header,
    search: serverAppSchema.shape.search,
    branding: brandingSchema,
    connectivity: connectivitySchema,
    changelog: changelogTenantPolicySchema,
  })
  .strict()

type StructureConfig = {
  navigation?: z.infer<typeof serverNavigationSchema>
  sections?: Array<z.infer<typeof configuredSectionSchema>>
  pages?: Array<z.infer<typeof configuredPageSchema>>
  monitoring?: z.infer<typeof monitoringYamlSchema>
}

function validateStructure(config: StructureConfig, context: z.RefinementCtx) {
  if (!config.sections && !config.pages) {
    context.addIssue({ code: 'custom', message: 'sections ou pages est requis' })
    return
  }
  if (config.sections && config.pages)
    context.addIssue({
      code: 'custom',
      message: 'sections et pages ne peuvent pas être utilisés ensemble',
    })
  const pages = config.pages ?? [
    { id: 'accueil', path: '/', title: '', sections: config.sections ?? [] },
  ]
  const pageIds = new Set<string>()
  const pagePaths = new Set<string>()
  const sectionIds = new Set<string>()
  const applicationIds = new Set<string>()
  const applicationsById = new Map<string, string>()
  const widgetIds = new Set<string>()
  const monitoringServiceIds = new Set<string>()
  config.monitoring?.services.forEach((service, index) => {
    if (monitoringServiceIds.has(service.id))
      context.addIssue({
        code: 'custom',
        path: ['monitoring', 'services', index, 'id'],
        message: `service supervisé dupliqué : ${service.id}`,
      })
    monitoringServiceIds.add(service.id)
  })
  pages.forEach((page, pageIndex) => {
    const pagePath: Array<string | number> = config.pages ? ['pages', pageIndex] : ['sections']
    if (pageIds.has(page.id))
      context.addIssue({
        code: 'custom',
        path: [...pagePath, 'id'],
        message: `identifiant de page dupliqué : ${page.id}`,
      })
    if (pagePaths.has(page.path))
      context.addIssue({
        code: 'custom',
        path: [...pagePath, 'path'],
        message: `chemin de page dupliqué : ${page.path}`,
      })
    pageIds.add(page.id)
    pagePaths.add(page.path)
    if (page.layout !== 'dashboard' && page.dashboard)
      context.addIssue({
        code: 'custom',
        path: [...pagePath, 'dashboard'],
        message: 'Un dashboard nécessite layout: dashboard',
      })
    if (page.layout === 'dashboard' && !page.dashboard)
      context.addIssue({
        code: 'custom',
        path: [...pagePath, 'dashboard'],
        message: 'Configuration dashboard requise',
      })
    page.sections.forEach((section, sectionIndex) => {
      const sectionPath = config.pages
        ? [...pagePath, 'sections', sectionIndex]
        : ['sections', sectionIndex]
      if (sectionIds.has(section.id))
        context.addIssue({
          code: 'custom',
          path: [...sectionPath, 'id'],
          message: `identifiant de section dupliqué : ${section.id}`,
        })
      sectionIds.add(section.id)
      if (section.type === 'message' && section.pages)
        section.pages
          .filter((path) => path !== '*')
          .forEach((path) => {
            if (!pages.some((candidate) => candidate.path === path))
              context.addIssue({
                code: 'custom',
                path: [...sectionPath, 'pages'],
                message: `page inconnue : ${path}`,
              })
          })
      if (section.type !== 'applications') return
      const localApplicationIds = new Set<string>()
      section.applications.forEach((application, applicationIndex) => {
        const previous = applicationsById.get(application.id)
        const value = JSON.stringify(application)
        if (localApplicationIds.has(application.id) || (previous && previous !== value))
          context.addIssue({
            code: 'custom',
            path: [...sectionPath, 'applications', applicationIndex, 'id'],
            message: `identifiant d’application dupliqué : ${application.id}`,
          })
        else if (!previous) applicationsById.set(application.id, value)
        localApplicationIds.add(application.id)
        applicationIds.add(application.id)
      })
    })
  })
  pages.forEach((page, pageIndex) =>
    page.dashboard?.widgets.forEach((widget, widgetIndex) => {
      const path = ['pages', pageIndex, 'dashboard', 'widgets', widgetIndex]
      if (widgetIds.has(widget.id))
        context.addIssue({
          code: 'custom',
          path: [...path, 'id'],
          message: `Identifiant de widget dupliqué : ${widget.id}`,
        })
      widgetIds.add(widget.id)
      if (widget.type === 'page-link' && !pageIds.has(widget.config.pageId))
        context.addIssue({
          code: 'custom',
          path: [...path, 'config', 'pageId'],
          message: 'Page référencée inconnue',
        })
      if (
        widget.type === 'application-shortcut' &&
        !applicationIds.has(widget.config.applicationId)
      )
        context.addIssue({
          code: 'custom',
          path: [...path, 'config', 'applicationId'],
          message: 'Application référencée inconnue',
        })
      if (widget.type === 'application-list')
        widget.config.applicationIds.forEach((id) => {
          if (!applicationIds.has(id))
            context.addIssue({
              code: 'custom',
              path: [...path, 'config', 'applicationIds'],
              message: `Application référencée inconnue : ${id}`,
            })
        })
      if (
        widget.action.type === 'link' &&
        widget.action.destination === 'internal' &&
        !pageIds.has(widget.action.pageId)
      )
        context.addIssue({
          code: 'custom',
          path: [...path, 'action', 'pageId'],
          message: 'Page référencée inconnue',
        })
    }),
  )
  config.monitoring?.services.forEach((service, index) => {
    if (service.applicationId && !applicationIds.has(service.applicationId))
      context.addIssue({
        code: 'custom',
        path: ['monitoring', 'services', index, 'applicationId'],
        message: `application inconnue : ${service.applicationId}`,
      })
  })
  if (config.pages && !pagePaths.has('/'))
    context.addIssue({
      code: 'custom',
      path: ['pages'],
      message: 'une page avec le chemin / est requise',
    })
  const navigationLinks = Array.isArray(config.navigation)
    ? config.navigation
    : (config.navigation?.links ?? [])
  const navigationIds = new Set<string>()
  navigationLinks.forEach((item, index) => {
    const itemPath = Array.isArray(config.navigation)
      ? ['navigation', index]
      : ['navigation', 'links', index]
    if ('path' in item && !pagePaths.has(item.path))
      context.addIssue({
        code: 'custom',
        path: [...itemPath, 'path'],
        message: `aucune page ne correspond au chemin ${item.path}`,
      })
    const itemId = 'id' in item ? item.id : pages.find((page) => page.path === item.path)?.id
    if (itemId && navigationIds.has(itemId))
      context.addIssue({
        code: 'custom',
        path: ['navigation'],
        message: `identifiant de navigation dupliqué : ${itemId}`,
      })
    if (itemId) navigationIds.add(itemId)
  })
}

export const serverMorningConfigSchema = z
  .object({
    app: serverAppSchema,
    navigation: serverNavigationSchema.optional(),
    sections: z.array(configuredSectionSchema).max(30).optional(),
    pages: z.array(configuredPageSchema).min(1).max(20).optional(),
    monitoring: monitoringYamlSchema.optional(),
    inventory: inventoryYamlSchema.optional(),
  })
  .strict()
  .superRefine(validateStructure)
export const publicMorningConfigSchema = z
  .object({
    app: publicAppSchema,
    navigation: publicNavigationSchema,
    pages: z.array(pageSchema).min(1).max(20),
  })
  .strict()

export const morningConfigSchema = serverMorningConfigSchema
